import { useQuery } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';

import type { Volume } from '~/shared/types/api';
import type { JsonLogicRule } from '../../utils/parseJsonLogicForTable';

export interface VolumeCategory {
  volumes: Volume[];
  count: number;
  totalCapacity: number;
  averageUtilization: number;
}

export interface RuleDryRunResponse {
  datafiable: VolumeCategory;
  potentiallyDatafiable: VolumeCategory;
  excluded: VolumeCategory;
}

interface DryRunRequestBody {
  active: boolean;
  rule: JsonLogicRule;
}

/**
 * Hook to fetch dry run data for a specific rule
 */
export function useRuleDryRun(rule: JsonLogicRule | null, enabled: boolean = true) {
  const api = useXFetch();

  return useQuery<RuleDryRunResponse>({
    queryKey: ['ruleDryRun', rule],
    enabled: enabled && rule !== null,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!rule) {
        throw new Error('No rule provided');
      }

      const requestBody: DryRunRequestBody = {
        active: true,
        rule,
      };

      // console.log('📊 Fetching dry run for rule:', JSON.stringify(requestBody, null, 2));

      const response = await api<RuleDryRunResponse>('/api/v1/volumes/autoscaling/rules/dry-run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      // console.log('✅ Dry run response:', response);

      return response;
    },
  });
}
